import {
  MDBAccordion,
  MDBAccordionItem,
  MDBBtn,
  MDBBtnGroup,
  MDBCol,
  MDBInput,
  MDBRow,
  MDBSelect,
  MDBTextArea
} from 'mdb-react-ui-kit';
import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { fonts, headers, textTypes, themeColors } from './Common';

// Utility functions for color generation and contrast
const getRandomColor = () => {
  let color, brightness;
  do {
    color = '#'+Math.floor(Math.random()*16777215).toString(16);
    const r = parseInt(color.substring(1, 3), 16);
    const g = parseInt(color.substring(3, 5), 16);
    const b = parseInt(color.substring(5, 7), 16);
    brightness = (r * 299 + g * 587 + b * 114) / 1000;
  } while (brightness > 250);
  return color;
};
const getContrastTextColor = (bgColor) => {
  const r = parseInt(bgColor.substring(1, 3), 16);
  const g = parseInt(bgColor.substring(3, 5), 16);
  const b = parseInt(bgColor.substring(5, 7), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 155 ? '#000000' : '#FFFFFF';
};

// Component to handle recursive paragraph rendering
// eslint-disable-next-line react/display-name
const RecursiveParagraph = memo(({ textData = [], setCurrentItem, setFormData, featureIndex , onParagraphToggle,  featureName,zIndex,contextType}) => {
  

  const [localTextData, setLocalTextData] = useState(textData);
  const [colors, setColors] = useState([]);

  const getFeatureByPath = (orchestration, featureIndex) => {
    const path = typeof featureIndex === 'string' && featureIndex.includes('-')
      ? featureIndex.split('-').map((i) => parseInt(i, 10))
      : [parseInt(featureIndex, 10)];

    let targetFeature = orchestration[path[0]];
    for (let i = 1; i < path.length; i++) {
      targetFeature = targetFeature.subFeatures ? targetFeature.subFeatures[path[i]] : null;
      if (!targetFeature) {
        console.error("Sub-feature not found at path", featureIndex);
        return null;
      }
    }
    return targetFeature;
  };

  useEffect(() => {
    setLocalTextData(textData);
  }, [textData]);

  useEffect(() => {
    setColors((prevColors) => {
      const newColors = { ...prevColors };
      localTextData.forEach((paragraph) => {
        if (paragraph && paragraph.id && !newColors[paragraph.id]) {
          newColors[paragraph.id] = getRandomColor();
        }
      });
      return newColors;
    });
  }, [localTextData]);

  const handleAddParagraph = useCallback(
    (currentParagraph) => {
      const newParagraph = {
        id: uuidv4(),
        textValue: '',
        heading: { id: uuidv4(), textValue: '', textColor: '', textSize: 'h3', fontSize: 'fs-6', type: 'text' },
        paragraphs: [],
        textColor: '',
        textSize: 'h1',
        fontSize: 'fs-6',
        type: 'text',
        orderNumber: (currentParagraph.paragraphs?.length || 0) + 1,
      };
  
      const addParagraphToTarget = (paragraphs, targetId, newParagraph) => {
        return paragraphs.map((paragraph) => {
          if (paragraph.id === targetId) {
            return { ...paragraph, paragraphs: [...(paragraph.paragraphs || []), newParagraph] };
          }
          if (paragraph.paragraphs && paragraph.paragraphs.length > 0) {
            return { ...paragraph, paragraphs: addParagraphToTarget(paragraph.paragraphs, targetId, newParagraph) };
          }
          return paragraph;
        });
      };
  
      if (setFormData) {
        setFormData((prevFormData) => {
          const updatedOrchestration = [...prevFormData.orchestration];
          const targetFeature = getFeatureByPath(updatedOrchestration, featureIndex);
  
          if (!targetFeature) {
            console.error("Target feature is not found");
            return prevFormData;
          }
  
          switch (contextType) {
            case "paragraphDescriptions":
              targetFeature.paragrapDescriptions = targetFeature.paragrapDescriptions.map((description) => {
                if (description.id === currentParagraph.id) {
                  return { ...description, paragraphs: [...(description.paragraphs || []), newParagraph] };
                }
                return { ...description, paragraphs: addParagraphToTarget(description.paragraphs || [], currentParagraph.id, newParagraph) };
              });
              break;
            case "pain":
            case "promise":
              targetFeature.painPromise = targetFeature.painPromise.map((promise) => {
                if (contextType === "pain" && promise.pain.id === currentParagraph.id) {
                  return {
                    ...promise,
                    pain: { ...promise.pain, paragraphs: [...(promise.pain.paragraphs || []), newParagraph] },
                  };
                }
                if (contextType === "promise" && promise.promise.id === currentParagraph.id) {
                  return {
                    ...promise,
                    promise: { ...promise.promise, paragraphs: [...(promise.promise.paragraphs || []), newParagraph] },
                  };
                }
                return promise;
              });
              break;
            default:
              console.error("Unknown context type");
          }
  
          return { ...prevFormData, orchestration: updatedOrchestration };
        });
      } else if (setCurrentItem) {
        setCurrentItem((prevItem) => {
          const updatedDescriptions = prevItem.descriptions.map((description) => {
            if (description.id === currentParagraph.id) {
              return { ...description, paragraphs: [...(description.paragraphs || []), newParagraph] };
            }
            return { ...description, paragraphs: addParagraphToTarget(description.paragraphs || [], currentParagraph.id, newParagraph) };
          });
          return { ...prevItem, descriptions: updatedDescriptions };
        });
      }
  
      setLocalTextData((prevTextData) => {
        return prevTextData.map((paragraph) => {
          if (paragraph.id === currentParagraph.id) {
            return { ...paragraph, paragraphs: [...(paragraph.paragraphs || []), newParagraph] };
          }
          return paragraph;
        });
      });
  
      setColors((prevColors) => ({
        ...prevColors,
        [newParagraph.id]: getRandomColor(),
      }));
    },
    [setFormData, setCurrentItem, featureIndex, contextType]
  );
  

  const handleTextChange = useCallback(
    (id, value) => {
      const updateParagraphById = (paragraphs, id, value) => {
        if (!paragraphs) return [];
        return paragraphs.map((paragraph) => {
          if (paragraph.id === id) {
            return { ...paragraph, ...value };
          }
          if (paragraph.paragraphs && paragraph.paragraphs.length > 0) {
            return { ...paragraph, paragraphs: updateParagraphById(paragraph.paragraphs, id, value) };
          }
          return paragraph;
        });
      };
  
      if (setFormData) {
        setFormData((prevFormData) => {
          const updatedOrchestration = [...prevFormData.orchestration];
          const targetFeature = getFeatureByPath(updatedOrchestration, featureIndex);
  
          if (!targetFeature) {
            console.error("Target feature is not found");
            return prevFormData;
          }
  
          switch (contextType) {
            case "paragraphDescriptions":
              targetFeature.paragrapDescriptions = updateParagraphById(targetFeature.paragrapDescriptions, id, value);
              break;
            case "pain":
            case "promise":
              targetFeature.painPromise = targetFeature.painPromise.map((promise) => {
                if (contextType === "pain") {
                  return { ...promise, pain: updateParagraphById([promise.pain], id, value)[0] };
                }
                if (contextType === "promise") {
                  return { ...promise, promise: updateParagraphById([promise.promise], id, value)[0] };
                }
                return promise;
              });
              break;
            default:
              console.error("Unknown context type");
          }
  
          return { ...prevFormData, orchestration: updatedOrchestration };
        });
      } else if (setCurrentItem) {
        setCurrentItem((prevItem) => {
          // Assuming setCurrentItem directly updates the item based on the id and value
          const updatedDescriptions = updateParagraphById(prevItem.descriptions, id, value);
          return { ...prevItem, descriptions: updatedDescriptions };
        });
      }
  
      setLocalTextData((prevLocalTextData) => {
        return updateParagraphById(prevLocalTextData, id, value);
      });
    },
    [setFormData, setCurrentItem, featureIndex, contextType]
  );
  
  const removeCurrentParagraph = useCallback(
    (paragraphId) => {
      const removeParagraphById = (items, id) => {
        return items.filter(item => item.id !== id).map(item => ({
          ...item,
          paragraphs: removeParagraphById(item.paragraphs || [], id)
        }));
      };
  
      if (setFormData) {
        setFormData((prevFormData) => {
          const updatedOrchestration = [...prevFormData.orchestration];
          const targetFeature = getFeatureByPath(updatedOrchestration, featureIndex);
  
          if (!targetFeature) {
            console.error("Feature not found");
            return prevFormData;
          }
  
          switch (contextType) {
            case "paragraphDescriptions":
              targetFeature.paragrapDescriptions = removeParagraphById(targetFeature.paragrapDescriptions, paragraphId);
              break;
            case "pain":
            case "promise":
              targetFeature.painPromise = targetFeature.painPromise.map((promise) => {
                if (contextType === "pain") {
                  return { ...promise, pain: { ...promise.pain, paragraphs: removeParagraphById(promise.pain.paragraphs || [], paragraphId) }};
                }
                if (contextType === "promise") {
                  return { ...promise, promise: { ...promise.promise, paragraphs: removeParagraphById(promise.promise.paragraphs || [], paragraphId) }};
                }
                return promise;
              });
              break;
            default:
              console.error("Unknown context type");
          }
  
          return { ...prevFormData, orchestration: updatedOrchestration };
        });
      } else if (setCurrentItem) {
        setCurrentItem((prevItem) => {
          const updatedDescriptions = removeParagraphById(prevItem.descriptions, paragraphId);
          return { ...prevItem, descriptions: updatedDescriptions };
        });
      }
  
      setLocalTextData((prevLocalTextData) => {
        return removeParagraphById(prevLocalTextData, paragraphId);
      });
    },
    [setFormData, setCurrentItem, featureIndex, contextType]
  );
  
  
  const truncateText = (text, maxLength) => {
    if (text.length > maxLength) {
      return text.substring(0, maxLength) + '...';
    }
    return text;
  };
    return (
<>
  {/* Use parentId if provided, otherwise generate a new ID based on the current context */}
  {localTextData?.length > 0 && localTextData.map((text, index) => {
      if (!text || !text.id) return null; // Skip rendering if text or id is missing
      
    
      const paragraphId = text?.id;
        const cardColor = colors[paragraphId] || getRandomColor();
        const contrastTextColor = getContrastTextColor(cardColor);

    return (
      <MDBAccordion key={paragraphId} style={{ position: 'relative', zIndex: 12 + zIndex,  margin: 0, padding: 0 }}>
        <MDBAccordionItem 
          collapseId={paragraphId}
          headerTitle={truncateText(text?.heading?.textValue || text?.textValue || '', 100)} // Adjust maxLength as needed
          onClick={() => onParagraphToggle && onParagraphToggle()}
          style={{ margin: 0, padding: 0 }}
        >
          <div
            className="mb-4 p-3 rounded text-black text-center d-flex flex-column justify-content-center align-items-center"
            style={{ backgroundColor: cardColor, color: contrastTextColor  }}
          >
    {/* Heading Section */}
    <div className="w-100 justify-content-center align-items-center">
      <h5 style={{ color: contrastTextColor }}>Heading Of Paragraph</h5>
      <MDBRow className="mt-2 mb-2 justify-content-center align-items-center">
      <MDBCol xs="12" sm="6" lg="6" className="mb-2">
      <MDBBtnGroup className='d-flex'>
            <MDBTextArea
              contrast
              className="mb-2"
              label="Heading Text Value"
              value={text?.heading?.textValue || ''}
              onChange={(e) => handleTextChange(text.id, { heading: { ...text.heading, textValue: e.target.value } })}

              
              style={{ color: contrastTextColor }}
            />
            
            {/* Clear Button for MDBInput */}
            <MDBBtn type='button' color="danger" size="sm" onClick={() => handleTextChange(text.id, {heading: { ...text.heading, textValue: ''}})}>
              x
            </MDBBtn>
          </MDBBtnGroup>
          </MDBCol>
          <MDBCol xs="12" sm="6" lg="6" className="mb-2">
            <MDBBtnGroup className='d-flex'>
              <MDBTextArea
                contrast
                className="mb-2"
                label="image to be displayed at left"
                value={text?.heading?.image || ''}
              
                onChange={(e) => handleTextChange(text.id, { heading: { ...text.heading, image: e.target.value } })}
                style={{ color: contrastTextColor }}
              />
              {/* Clear Button for MDBInput */}
              <MDBBtn type='button' color="danger" size="sm" onClick={() => handleTextChange(text.id, { heading: { ...text.heading, image: '' } })}>

                x
              </MDBBtn>
              
            </MDBBtnGroup>
            </MDBCol>          
          </MDBRow>
          <MDBRow className="mt-2 justify-content-center">
  <MDBCol xs="12" sm="6" lg="3" className="mb-2">
    <MDBSelect
      clearBtn
      autoSelect
      contrast
      label="Heading Text Color"
      data={themeColors || []}
      value={text?.heading?.textColor || 'primary'}   
      onValueChange={(selected) => {
        if (selected && selected.value) {
        handleTextChange(text.id, { heading: { ...text.heading, textColor: selected.value } });
        }
      }}


    />
  </MDBCol>
  <MDBCol xs="12" sm="6" lg="3" className="mb-2">
    <MDBSelect
      autoSelect
      contrast
      label="Header Heading Size"
      data={headers || []}
      value={text?.heading?.textSize || 'h3'}  
      onValueChange={(selected) => {
        if (selected && selected.value) {
        handleTextChange(text.id, { heading: { ...text.heading, textSize: selected.value } });
        }
      }}

    />
  </MDBCol>
  <MDBCol xs="12" sm="6" lg="3" className="mb-2">
  <MDBSelect
  contrast
  clearBtn
  autoSelect
  label="Header Font Size"
  data={fonts || []}
  value={text?.heading?.fontSize || 'fs-4'} // Ensure the value is set properly
  onValueChange={(selected) => {
    if (selected && selected.value) {
      // Handle the change and set the updated value
      handleTextChange(text.id, { heading: { ...text.heading, fontSize: selected.value } });
    }
  }}
/>

  </MDBCol>

  <MDBCol xs="12" sm="6" lg="3" className="mb-2">
    <MDBSelect autoSelect  contrast  label="Type Of Text in Header"  data={textTypes || []}  value={text?.heading?.type || 'text'}  onValueChange={(selected) => {
        if (selected && selected.value) {
        handleTextChange(text.id, { heading: { ...text.heading, type: selected.value } });
        }
      }}

    />
  </MDBCol>
</MDBRow>

    </div>
    <div className="w-100 justify-content-center align-items-center">
    <h5 style={{ color: contrastTextColor }}>Body Of Paragraph</h5>
    <div className="w-100 mb-2">
    <MDBBtnGroup className='d-flex'>
            <MDBTextArea
              contrast
              className="mt-2"
              label="Main Text Value"
              value={text?.textValue || ''}
             // onChange={(e) => handleTextChange(index, 'textValue', e.target.value || '')}
              onChange={(e) => handleTextChange(text.id, { textValue: e.target.value || '' })} 
              style={{ color: contrastTextColor }}
            />
            {/* Clear Button for MDBTextArea */}
            <MDBBtn type='button' color="danger" size="sm" onClick={() => handleTextChange(text.id, { textValue: '' })}>
              x
            </MDBBtn>
          </MDBBtnGroup>

      <MDBRow className="mt-2 mb-2 justify-content-center">
        <MDBCol xs="12" sm="6" lg="2" className="mb-2">
          <MDBSelect  clearBtn   autoSelect
            contrast
            label="Main Text Color"
            data={themeColors || []}
            value={text?.textColor || ''}
          
            onValueChange={(selected) => {
              if (selected && selected.value) {
              handleTextChange(text.id, { textColor: selected.value });
              }
            }}
            
          />
        </MDBCol>
       
       
        <MDBCol xs="12" sm="6" lg="2" className="mb-2">
  <MDBSelect
    clearBtn
    autoSelect
    contrast
    label="Main Header Size"
    data={headers || []}
    value={text?.textSize || 'h5'}
    onValueChange={(selected) => {
      if (selected && selected.value) {
      handleTextChange(text.id, { textSize: selected.value });
      }
    }} 
  />
</MDBCol>
<MDBCol xs="12" sm="6" lg="3" className="mb-2">
  <MDBSelect
    autoSelect
    contrast
    label="Main Font Size"
    data={fonts || []}
    value={text?.fontSize || 'fs-6'}
    onValueChange={(selected) => {
     
      if (selected && selected.value) {
        if (selected && selected.value) {
        handleTextChange(text.id, { fontSize: selected.value });
        }
      }
    }}
  />
</MDBCol>

        <MDBCol xs="12" sm="6" lg="2" className="mb-2">
          <MDBSelect  clearBtn   autoSelect
            contrast
            label="Type Of Text in Main"
            data={textTypes || []}
            value={text?.type || 'text'}
            onValueChange={(selected) => {
              if (selected && selected.value) {
              handleTextChange(text.id, { type: selected.value });
              }
            }} 
          />
        </MDBCol>
        <MDBCol xs="12" sm="6" lg="2" className="mb-2">
          <MDBInput  clearBtn
            contrast min={1}
            label="Order Of This Paragraph"
            id="slideNumber"
            type="number"removeParagraph
            value={text?.orderNumber}
            //onChange={(e) => handleTextChange(index, 'orderNumber', e.target.value)}
            onChange={(e) => handleTextChange(text.id, { orderNumber: parseInt(e.target.value, 10) })}


          />



          
        </MDBCol>
      </MDBRow>
      {text.paragraphs && text.paragraphs.length > 0 && (
  <div style={{ maxHeight: 'auto', overflowY: 'visible' }}>
    {text.paragraphs.map((paragraph) => (
      setFormData ? (
        <RecursiveParagraph
          key={paragraph.id}
          textData={[paragraph]}
          setFormData={setFormData} 
          featureName={featureName}       
          featureIndex={featureIndex}
          zIndex={paragraph.paragraphs.length}
          contextType={contextType}
        />
      ) : setCurrentItem ? (
        <RecursiveParagraph
          key={paragraph.id}
          featureName={paragraph?.textValue || paragraph?.heading?.textValue || ''}
          textData={[paragraph]}
          setCurrentItem={setCurrentItem} // Directly pass setCurrentItem
          featureIndex={featureIndex}
          zIndex={paragraph.paragraphs.length}
          contextType={contextType}
        />
      ) : null
    ))}
  </div>
)}




    </div>
</div>
    {/* Add and Remove Buttons */}
    <MDBBtnGroup>
                  <MDBBtn
                    type="button"
                    size="sm"
                    onClick={() => handleAddParagraph(text)}
                  >
                    Add Paragraph
                  </MDBBtn>
                  <MDBBtn type="button" size="sm" color="danger" onClick={() => removeCurrentParagraph(text.id)}>
  Remove Paragraph
</MDBBtn>




                </MDBBtnGroup>

  </div>

                  {/* <MDBBtn type="button" size="sm" color="danger" onClick={() => handleRemoveDescription(featureIndex, text.id)}>
  Remove Description
</MDBBtn> */}

  </MDBAccordionItem> 
  </MDBAccordion>  
          );
        })}
          
     
  

      </>
    );
  }, (prevProps, nextProps) => {
    return (
      JSON.stringify(prevProps.textData) === JSON.stringify(nextProps.textData) &&
      prevProps.maxDepth === nextProps.maxDepth &&
      prevProps.parentKey === nextProps.parentKey
    );
  });
  
  RecursiveParagraph.propTypes = {
    textData: PropTypes.array.isRequired, // The array of descriptions/paragraphs
    updateParentData: PropTypes.func.isRequired, // Function to update parent data
    featureIndex: PropTypes.number.isRequired,
    parentKey: PropTypes.string, // For nested paragraphs (optional)
    onParagraphToggle: PropTypes.func,
    maxDepth: PropTypes.number,
    initialDescIndex: PropTypes.number,
    initialParagraphIndex: PropTypes.number,
    handleRemoveDescription: PropTypes.func.isRequired,
    setFormData: PropTypes.func,
    parentId: PropTypes.string,
    parentParagraphName: PropTypes.string,
    featureName: PropTypes.string,
    paragraphIndex: PropTypes.number,
    parentPath: PropTypes.array,
    zIndex: PropTypes.number,
    setCurrentItem: PropTypes.func,
    contextType: PropTypes.string,
  };

  
  export default RecursiveParagraph;
  